import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { getControl, getFormArray, getGroup } from '@shared/helpers/form.helper';
import { formPaths } from '@shared/constants/form-paths.const';
import { withChangeDebounced } from '@shared/operators/with-change-debounced.operator';
import { MyElSocietaPartecipate, MyVerbale } from '../../../models/interfaces/revisione.interface';
import { toNum } from '@shared/helpers/math.helper';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { cloneDeep } from 'lodash-es';
import { debounceTime, Observable, startWith } from 'rxjs';
import { perListenerSession } from '@shared/operators/listener-session';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { Utilities } from '@core/helpers/utilities';
import { calcCertBilancioPatch } from '../common/helpers/cert-bilancio.helper';

export const datiBilancioBusinessListeners = (form: FormGroup, store: Store<Revisione53State>): void => {
    my_ElSocietaPartecipate(form, store);
    my_NumBilanciInExcel(form, store);
};

/** Clears my_PercSpaSrl for rows where my_TipoSocietaPartec is s.a.p.a./Altro (percentage doesn't apply there),
 *  then updates my_PartecPiuDi50InSpa in both. Se my_PartecPiuDi50InSpa cambia, ricalcola l'obbligo di certificazione
 *  (InfoPath msoxd_my_PartecPiuDi50InSpa_OnAfterChange: aggiornoEnableCkEnteTenutoCertBil + aggiornaCkEnteTenutoCertBil).
 *  Cross-section my_CkPeriodicRevi write is handled by the dati-identificativi builders. */
const my_ElSocietaPartecipate = (form: FormGroup, store: Store<Revisione53State>): void => {
    const formArray = getFormArray(form, `${formPaths.bilancio}.my_SocietaPartecipate.my_ElSocietaPartecipate`);
    formArray.valueChanges.pipe(withChangeDebounced(formArray)).subscribe((elements: MyElSocietaPartecipate[]) => {
        let partecPiuDi50 = '0';
        (elements ?? []).forEach((element, index) => {
            if (element.my_TipoSocietaPartec !== '1' && element.my_TipoSocietaPartec !== '2') {
                element.my_PercSpaSrl = null;
                getControl(formArray.at(index) as FormGroup, 'my_PercSpaSrl')?.patchValue('', { emitEvent: false });
            }
            if (element.my_TipoSocietaPartec === '1' && toNum(element.my_PercSpaSrl) > 50) {
                partecPiuDi50 = '1';
            }
        });
        getControl(form, 'my_PartecPiuDi50InSpa').patchValue(partecPiuDi50, { emitEvent: false });
        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            const partecChanged = myVerbale.my_PartecPiuDi50InSpa !== partecPiuDi50;
            myVerbaleClone.my_PartecPiuDi50InSpa = partecPiuDi50;
            const bilancio = myVerbaleClone.my_SezioneI__Rilevazione.my_Bilancio;
            bilancio.my_SocietaPartecipate.my_ElSocietaPartecipate = elements;
            if (partecChanged) {
                const certPatch = calcCertBilancioPatch(myVerbaleClone);
                Object.assign(bilancio, certPatch);
                getGroup(form, formPaths.bilancio).patchValue(certPatch);
            }
            return myVerbaleClone;
        });
    });
};

const DEFAULTS_WHEN_ZERO = { my_BilAnno1: '1900', my_BilAnno2: '1900', my_DataChiusura1: '1900-12-31', my_DataChiusura2: '1900-12-31' };
const DEFAULTS_WHEN_NOT_ZERO = { my_BilAnno1: '', my_BilAnno2: '', my_DataChiusura1: '', my_DataChiusura2: '' };

const isBlankValue = (v: unknown): boolean => v == null || (typeof v === 'string' && v.trim() === '');

/** Sets my_DatiBilancio year/date defaults when my_NumBilanciInExcel changes.
 *  Skips the patch when any target field is already non-blank, to avoid overwriting existing data. */
const my_NumBilanciInExcel = (form: FormGroup, store: Store<Revisione53State>): void => {
    const datiBilancioGroup = getGroup(form, formPaths.datiBilancio);
    const bilancioGroup = getGroup(form, formPaths.bilancio);
    const numBilanciCtrl = getControl(bilancioGroup, 'my_NumBilanciInExcel');

    const allTargetsBlank = (): boolean => {
        const cur = datiBilancioGroup.getRawValue?.() ?? datiBilancioGroup.value;
        return isBlankValue(cur?.my_BilAnno1) && isBlankValue(cur?.my_BilAnno2) && isBlankValue(cur?.my_DataChiusura1) && isBlankValue(cur?.my_DataChiusura2);
    };

    numBilanciCtrl.valueChanges
        .pipe(
            // Sessione per verbale: al restart la memoria di distinctUntilChanged si azzera e il valore corrente viene rivalutato, come dopo un F5.
            perListenerSession((s$: Observable<unknown>) =>
                s$.pipe(
                    startWith(numBilanciCtrl.value),
                    debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME),
                    map((v) => {
                        const s = String(v ?? '').trim();
                        if (s === '') return null;
                        return s === '0';
                    }),
                    filter((x): x is boolean => x !== null),
                    distinctUntilChanged()
                )
            )
        )
        .subscribe((isZero) => {
            if (!allTargetsBlank()) {
                return;
            }
            const defaults = isZero ? DEFAULTS_WHEN_ZERO : DEFAULTS_WHEN_NOT_ZERO;
            datiBilancioGroup.patchValue(defaults, { emitEvent: false });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                const datiBil = myVerbaleClone.my_SezioneI__Rilevazione.my_DatiBilancio;
                datiBil.my_BilAnno1 = defaults.my_BilAnno1;
                datiBil.my_BilAnno2 = defaults.my_BilAnno2;
                datiBil.my_DataChiusura1 = defaults.my_DataChiusura1;
                datiBil.my_DataChiusura2 = defaults.my_DataChiusura2;
                return myVerbaleClone;
            });
        });
};
